/**
 * RFQ and vendor quotes API calls.
 */

import client, { fetchAllPages } from './client';

export async function fetchRfqs(params = {}) {
  // All pages — RfqTab.jsx pairs RFQs with quotes via buildBestQuoteMap
  // and Assign-PO logic; a truncated RFQ list would silently drop RFQ
  // groups on a heavy job (mirror of the fetchVendorQuotes bug Kevin
  // reported for quotes).
  return fetchAllPages('/rfqs/', params);
}

export async function fetchRfq(rfqId) {
  const { data } = await client.get(`/rfqs/${rfqId}/`);
  return data.data;
}

export async function generateRfqs(jobId) {
  const { data } = await client.post(`/rfqs/generate/`, { job_id: jobId });
  return data.data;
}

export async function sendRfq(rfqId) {
  const { data } = await client.post(`/rfqs/${rfqId}/send/`);
  return data.data;
}

export async function updateRfq(rfqId, payload) {
  const { data } = await client.patch(`/rfqs/${rfqId}/`, payload);
  return data.data;
}

export async function fetchVendorQuotes(params = {}) {
  // FIX (Kevin-reported): jobs with >25 quotes silently truncated —
  // Ruelco/Clear Kut quotes existed in DB but were invisible in
  // RfqTab because the caller reads through buildBestQuoteMap and
  // Assign-PO. Return ALL quotes for the filter.
  return fetchAllPages('/rfqs/vendor-quotes/', params);
}

export async function createManualQuote(payload) {
  const { data } = await client.post('/rfqs/manual-quote/', payload);
  return data.data;
}

export async function selectQuotes(selections) {
  const { data } = await client.post('/rfqs/select-quotes/', selections);
  return data.data;
}

export async function fetchRfqLines(params = {}) {
  const { data } = await client.get('/rfqs/lines/', { params });
  return data.data || data.results || [];
}

export async function fetchEmailMessage(emailId) {
  const { data } = await client.get(`/email/${emailId}/`);
  return data.data;
}

export async function fetchEmailAttachments(emailId) {
  const { data } = await client.get(`/email/${emailId}/attachments/`);
  return data.data || [];
}

export async function openAttachment(emailId, attachmentId, filename) {
  // Fetch the binary through the authenticated axios client, then open as a
  // blob URL so the browser can render it (PDFs open inline, others download).
  const response = await client.get(
    `/email/${emailId}/attachments/${attachmentId}/download/`,
    { responseType: 'blob' },
  );
  const blob = response.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // PDFs: open in new tab. Others: force download with filename.
  const isPdf = blob.type === 'application/pdf' || filename?.toLowerCase().endsWith('.pdf');
  if (isPdf) {
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
  } else {
    a.download = filename || 'attachment';
  }
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a short delay to allow the tab/download to start.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ── Resend & Auto-resend ─────────────────────────────────

export async function resendRfq(rfqId) {
  const { data } = await client.post(`/rfqs/${rfqId}/resend/`);
  return data.data;
}

export async function toggleAutoResend(rfqId) {
  const { data } = await client.post(`/rfqs/${rfqId}/toggle-auto-resend/`);
  return data.data;
}

export async function sendAllDrafts(jobId) {
  const { data } = await client.post('/rfqs/send-all-drafts/', { job_id: jobId });
  return data.data;
}

export async function fetchNeedsResponse() {
  const { data } = await client.get('/rfqs/needs-response/');
  return data;
}

export async function dismissResponse(emailId) {
  const { data } = await client.post('/rfqs/dismiss-response/', { email_id: emailId });
  return data;
}

// ── RFQ Attachments ────────────────────────────────────────

export async function fetchRfqAttachments(rfqId) {
  const { data } = await client.get(`/rfqs/${rfqId}/attachments/`);
  return data.data || [];
}

export async function uploadRfqAttachment(rfqId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post(`/rfqs/${rfqId}/attachments/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.data;
}

export async function deleteRfqAttachment(rfqId, fileId) {
  const { data } = await client.delete(`/rfqs/${rfqId}/attachments/${fileId}/`);
  return data.data;
}

// ── PO Assignment (Phase 26c) ──────────────────────────────

export async function assignRfqPo(rfqId, userId) {
  const { data } = await client.post(`/rfqs/${rfqId}/assign-po/`, { user_id: userId });
  return data.data;
}

export async function bulkAssignRfqPo(jobId, userId) {
  const { data } = await client.post('/rfqs/bulk-assign-po/', { job_id: jobId, user_id: userId });
  return data.data;
}

export async function completeRfqPo(rfqId) {
  const { data } = await client.post(`/rfqs/${rfqId}/complete-po/`);
  return data.data;
}
